import { z } from 'zod'

/**
 * For a response/output OBJECT or ARRAY container field (or a scalar NUMBER/BOOLEAN field) that
 * should accept an explicit JSON `null` at runtime (treating it exactly like an omitted field),
 * without that nullability ever appearing in the served JSON Schema.
 *
 * `.nullable()` looks like the obvious way to accept `null`, but it always shows up in the
 * generated schema as `type: [...,'null']`, and both `convertRjsfArraysToOneOf` (array containers)
 * and the scalar/object mappable-picker machinery only recognize a bare type when deciding whether
 * to build the individual-vs-mapped `oneOf` every other output field gets, so `.nullable()`
 * silently loses that. This is the exact tradeoff Jazz's `barcodes` field already documents
 * (`write-integration/integration/jazz/metadata.ts`), and the fix isn't to touch the shared
 * conversion logic. Doing so would touch every already-migrated integration AND every
 * hand-written schema across the codebase that happens to already declare `type: ['array','null']`
 * (dozens of them, e.g. `campfire.ts`, `quickbooks`, `stripe`), all with no review context.
 *
 * `z.preprocess()` coerces a `null` input to `undefined` BEFORE the declared schema is even
 * consulted, so the schema's own JSON representation never needs to express nullability at all,
 * while still genuinely accepting `null` at parse time. Verified: `z.toJSONSchema()` on the result
 * shows a bare type with no `required` leak, and `.safeParse()` accepts `null`, `undefined`, and a
 * real value identically to what `.optional()` alone accepted for `undefined`.
 */
export function omitNull<T extends z.ZodType>(
  schema: T,
): z.ZodOptional<z.ZodPipe<z.ZodTransform<object | undefined, unknown>, z.ZodOptional<T>>> {
  // The inner schema must itself accept `undefined`: after `preprocess` turns `null` into
  // `undefined`, that `undefined` still has to pass the WRAPPED schema's own validation before
  // the outer `.optional()` (which only covers the field being omitted from its parent object)
  // ever gets a say.
  return z.preprocess((value) => (value === null ? undefined : value), schema.optional()).optional()
}

/**
 * `.meta()` key `keepNull()` sets on its nullable node.
 * `toDataSchema()` (common-jobs) strips that node's `null` branch from the served schema.
 */
export const KEEP_NULL_SCHEMA_KEY = 'x-doss-keep-null'

/**
 * For a response/output field (a NUMBER/BOOLEAN scalar, or an OBJECT/ARRAY container) that should
 * accept an explicit JSON `null` AND keep it as `null` in the parsed value, like yup's
 * `.nullable().optional()` did, while the served schema still shows a bare type.
 *
 * Use this, not `omitNull()`, on a step OUTPUT. `omitNull()` parses `{x: null}` to `{}`, and
 * downstream steps tell the two apart: `resolveSelectionsAsync` passes a resolved `null` through
 * but treats `undefined` as "no value" (PHP-1140). `omitNull()` stays right for INPUTS, where a
 * mapped `null` deliberately means "not set".
 *
 * Parses as `.nullish()`. The served type stays bare because the mappable picker
 * (`normalizeNonStringTypes`) and the mapping options tree (`getExpandableTreeNodes`) only
 * recognize a bare `type`. The nullable node carries `KEEP_NULL_SCHEMA_KEY` in its `.meta()`, so
 * `toDataSchema()` can drop its `null` branch; a later `.meta()` on the returned schema doesn't
 * clobber it, since that lands on the outer `.optional()` node.
 */
export function keepNull<T extends z.ZodType>(schema: T): z.ZodOptional<z.ZodNullable<T>> {
  return schema
    .nullable()
    .meta({ [KEEP_NULL_SCHEMA_KEY]: true })
    .optional()
}

/**
 * For a mappable NUMBER field where `null` was never valid pre-migration on EITHER source, a
 * REQUIRED field, not a nullability-drift decision (that's `omitNull()`, for a field that should
 * treat `null` as "no value").
 *
 * `z.coerce.number()` doesn't reject `null` the way plain `z.number()` would: it calls `Number()`
 * on the input before the schema's own type check ever runs, and `Number(null) === 0`, a
 * perfectly valid number, so a bare `z.coerce.number()` silently turns a mapped `null` into `0`
 * instead of failing. This is a real runtime path, not a hypothetical: a mapped field's resolved
 * value can genuinely be `null`, not just missing, whenever the source it points to (a table
 * cell, a formula result) is itself null (`resolveSelectionsAsync`'s plain field-mapping branch in
 * `resolve-selections.ts` returns a resolved `null` unchanged, it only treats `undefined` as
 * absent).
 *
 * This preprocesses `null` into `NaN` before the wrapped schema runs. `NaN` IS rejected by Zod's
 * own number check (`invalid_type`), so a `null` input fails validation exactly the way it always
 * would have pre-migration, restoring "null is invalid" instead of silently producing the wrong
 * value. Every other input, a real number or a numeric string from a mapped field, passes through
 * untouched and still coerces normally.
 */
export function rejectNull<T extends z.ZodType>(
  schema: T,
): z.ZodPipe<z.ZodTransform<unknown, unknown>, T> {
  return z.preprocess((value: unknown): unknown => (value === null ? Number.NaN : value), schema)
}

/**
 * For a mappable BOOLEAN field. The mapped half of the picker is a `format: 'boolean'` string, so a
 * mapping resolves to `'true'`/`'false'`, which plain `z.boolean()` rejects. `safeValidateSync` is
 * fail-open, so the raw string then reaches the handler, where `'false'` is truthy.
 * yup's non-strict cast handled this, Zod doesn't.
 *
 * Also for a response BOOLEAN that the vendor sends as a string, e.g. HubSpot's CRM v3, which
 * returns every property value as a string (`hs_is_closed: 'false'`).
 *
 * Not `z.coerce.boolean()`, which turns `'false'` into `true`. Anything other than the two strings
 * passes through untouched for the wrapped schema to accept or reject.
 *
 * A field with a default needs it twice: `coerceStringBoolean(z.boolean().default(x)).default(x)`.
 * The inner one applies at parse time, the outer one keeps the field out of `required` while still
 * emitting `default` into the served schema (see postgres's `writeToGcs`).
 */
export function coerceStringBoolean<T extends z.ZodType>(
  schema: T,
): z.ZodPipe<z.ZodTransform<unknown, unknown>, T> {
  return z.preprocess((value: unknown): unknown => {
    if (value === 'true') {
      return true
    }
    if (value === 'false') {
      return false
    }
    return value
  }, schema)
}

/**
 * For a coerced number: `Number('') === 0` (whitespace too), so `z.coerce.number()` alone turns a
 * blank into `0`. yup cast it to `NaN` and rejected it. This turns a blank string into `undefined`
 * first: a required field then fails (`Number(undefined)` is `NaN`), and an optional one (inner
 * `.optional()`) is treated as unset. Prefer `optionalMappedNumber()` / `requiredMappedNumber()`,
 * which also guard `null`.
 */
export function blankAsMissing<T extends z.ZodType>(
  schema: T,
): z.ZodPipe<z.ZodTransform<unknown, unknown>, T> {
  return z.preprocess(
    (value: unknown): unknown =>
      typeof value === 'string' && value.trim() === '' ? undefined : value,
    schema,
  )
}

/**
 * For a coerced RESPONSE number: turns a blank string into `null` so `Number('')` can't make it `0`.
 * Wrap `keepNull()`, e.g. `blankAsNull(keepNull(z.coerce.number()))`, so `null` and blank both
 * parse to `null`.
 *
 * Not `blankAsMissing()`, which turns a blank into `undefined`. On an output that drops the key
 * once serialized, the missing-vs-`null` split PHP-1140 fixed. Inputs keep `blankAsMissing()`.
 *
 * The preprocess goes outside: `keepNull()`'s `.nullable()` only catches a real `null`, so a blank
 * inside it would still reach the coerce. The outer `.optional()` keeps the field out of
 * `required`, since a preprocess pipe hides the inner one.
 */
export function blankAsNull<T extends z.ZodType>(
  schema: T,
): z.ZodOptional<z.ZodPipe<z.ZodTransform<unknown, unknown>, T>> {
  return z
    .preprocess(
      (value: unknown): unknown =>
        typeof value === 'string' && value.trim() === '' ? null : value,
      schema,
    )
    .optional()
}

/**
 * For an OPTIONAL mappable number field, or a vendor response number: a `null` or blank string is
 * treated as unset. Pass the bare coerced number, e.g. `optionalMappedNumber(z.coerce.number().int())`,
 * and put `.meta()` on the result. Adds the inner `.optional()` `blankAsMissing()` needs itself.
 *
 * Don't use on a field with its own `.default(0)`: `null`/blank already land on that `0`, and
 * the wrappers would hide the default from `z.toJSONSchema()`, flipping the field to required.
 */
export function optionalMappedNumber<T extends z.ZodType<number>>(
  schema: T,
): z.ZodOptional<
  z.ZodPipe<
    z.ZodTransform<object | undefined, unknown>,
    z.ZodOptional<z.ZodPipe<z.ZodTransform<unknown, unknown>, z.ZodOptional<T>>>
  >
> {
  return omitNull(blankAsMissing(schema.optional()))
}

/**
 * For a REQUIRED mappable number field: a mapped `null` or blank string fails validation, as it
 * did under yup, instead of becoming `0`. See `rejectNull()` and `blankAsMissing()`.
 */
export function requiredMappedNumber<T extends z.ZodType<number>>(
  schema: T,
): z.ZodPipe<z.ZodTransform<unknown, unknown>, z.ZodPipe<z.ZodTransform<unknown, unknown>, T>> {
  return rejectNull(blankAsMissing(schema))
}
